import type { BackgroundValue } from '@elfsight/configurator-contracts';
import {
  type FreeLinkConfig,
  FreeLink as LibFreeLink,
  pickFreeLinkSettings
} from '@elfsight/widget-free-link';
import { getInvertedColor, useIsMobile } from '@elfsight/widget-utils';
import { useSettings } from '@wu/widget-core';
import { isDarkColor } from '../../../../../packages/lib-utils/src';
import { meta } from '../../constants';

export const FREE_LINK_HEIGHT = 21;
export const FREE_LINK_HEIGHT_ON_MOBILE = 41;

const BREAKPOINTS = {
  forTwoLines: 260,
  forWideComponent: 500
};

export function FreeLink({ popupShowing }: { popupShowing: boolean }) {
  const { layout, widgetBackground, widgetWidth, ...settings } = useSettings();
  const freeLinkSettings = pickFreeLinkSettings(settings);
  const isMobile = useIsMobile();

  const isSlideIn = layout === 'leftSlideIn' || layout === 'rightSlideIn';
  const isBar = layout === 'topBar' || layout === 'bottomBar';
  const isTopBar = layout === 'topBar';

  const isNarrowWidgetForFreeLink = widgetWidth < BREAKPOINTS['forTwoLines'];
  const isNarrowSlideIn = isSlideIn && (isMobile || isNarrowWidgetForFreeLink);

  const color = getColor(widgetBackground);
  const style = getStyle(isSlideIn, isBar, isTopBar, color, widgetWidth);

  return (
    <LibFreeLink
      {...freeLinkSettings}
      freeLinkAnchor={undefined} // use default template for anchor text
      displayFreeLink={freeLinkSettings.displayFreeLink && popupShowing}
      appAlias={meta.alias}
      appName={meta.name}
      mode={isBar || isSlideIn ? 'text' : 'button'}
      textModeProps={{
        color,
        forceTwoLines: isNarrowSlideIn,
        customColor:
          widgetBackground.type === 'image' || widgetBackground.type === 'video'
            ? 'rgba(113, 113, 113, 0.8)'
            : undefined,
        fontSize: 10
      }}
      iconHidden={isNarrowSlideIn}
      style={style}
    />
  );
}

function getStyle(
  isSlideIn: boolean,
  isBar: boolean,
  isTopBar: boolean,
  color: string,
  widgetWidth: number
): FreeLinkConfig['style'] {
  const backgroundAlphaValue = isDarkColor(color) ? 5 : 10;

  if (isBar) {
    return {
      link: {
        position: 'absolute',
        zIndex: 999999999,
        top: isTopBar ? 0 : 'auto',
        bottom: isTopBar ? 'auto' : 0,
        height: `${FREE_LINK_HEIGHT}px`,
        width: '100%',
        maxWidth: '100%',
        lineHeight: 1.3,
        margin: 0,
        padding: '4px 0',
        backgroundColor: `rgb(from ${color} r g b / ${backgroundAlphaValue}%)`,
        backdropFilter: 'blur(40px)'
      }
    };
  }

  if (isSlideIn) {
    const useWideView = widgetWidth <= BREAKPOINTS['forWideComponent'];

    const centerStyle = {
      link: {
        width: '100%'
      }
    };

    const wideStyle = {
      link: {
        left: 0,
        transform: 'none'
      },
      removeText: {
        marginLeft: 'auto'
      }
    };

    return {
      link: {
        position: 'absolute',
        zIndex: 999999999,
        bottom: 0,
        right: 0,
        margin: '8px',
        maxWidth: '100%',
        ...(useWideView ? wideStyle.link : centerStyle.link)
      },
      removeText: useWideView ? wideStyle.removeText : undefined
    };
  }

  return {
    link: {
      position: 'absolute',
      zIndex: 999999999,
      bottom: 0,
      width: 'max-content'
    }
  };
}

function getColor(widgetBackground: BackgroundValue) {
  const backgroundValue = getBackgroundValue(widgetBackground);
  const isTransparentBackground =
    widgetBackground.type === 'color' &&
    widgetBackground.color === 'transparent';

  if (widgetBackground.type === 'image' || widgetBackground.type === 'video') {
    return 'rgba(113, 113, 113, 0.8)';
  }

  if (!backgroundValue || isTransparentBackground) {
    return 'rgb(17, 17, 17)';
  }

  return getInvertedColor(backgroundValue);
}

function getBackgroundValue(background: BackgroundValue) {
  if (background.type === 'color') {
    return background.color;
  }

  if (background.type === 'gradient') {
    if (
      background.gradient!.direction > 90 &&
      background.gradient!.direction < 270
    ) {
      return background.gradient?.colors[0];
    }

    return background.gradient?.colors[1];
  }

  return;
}
